import type { ICurrentUser } from "models/ICurrentUser";
import { getCurrentUser } from "../../api";

/**
 * Single-flight cache for the signed-in user.
 *
 * The first caller starts the lookup, callers that arrive while it is in
 * flight await the same promise, and callers that arrive afterwards read the
 * settled outcome. The result is held for the lifetime of the page load,
 * rejections included, so the identity is fetched exactly once no matter how
 * many guards mount. A failed lookup ends in a login redirect or an error
 * page, both of which reload the page and reset the cache.
 *
 * The promise is parked on globalThis rather than in module scope because the
 * guard is reached through two different import specifiers (relative from
 * App, aliased from the per-app route files). A bundler that treats those as
 * separate modules would otherwise give each copy its own cache and each copy
 * would issue its own request.
 */
const cacheHost = globalThis as typeof globalThis & {
  aimsCurrentUserLookup?: Promise<ICurrentUser | null>;
};

export const resolveCurrentUser = (): Promise<ICurrentUser | null> => {
  if (!cacheHost.aimsCurrentUserLookup) {
    cacheHost.aimsCurrentUserLookup = getCurrentUser();
  }

  return cacheHost.aimsCurrentUserLookup;
};

export const clearCurrentUserCache = (): void => {
  delete cacheHost.aimsCurrentUserLookup;
};
